import { forwardRef, memo } from "react";
import { Form } from "react-bootstrap";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { slice } from "../../../Utils/stringUtils";
import { normalizeForFuzzy, isCloseMatch } from "../../../Utils/aiUtils";
import CustomTooltip from "../Tooltip/CustomTooltip";
import "./dropdown.scss";

const CustomInput = (props) => {
  const { onKeyDown, isAddNew, onNoOptionsClick, options, maxLength } = props.selectProps;
  return (
    <components.Input
      {...props}
      maxLength={maxLength}
      onKeyDown={(e) => {
        if (onKeyDown) onKeyDown(e);

        if ((e.key === "Tab" || e.key === "Enter") && isAddNew && onNoOptionsClick && props.value) {
          // Get the current filtered options from the select state
          const filteredOptions = props.selectProps.options || [];
          const hasMatches = filteredOptions.some((o) =>
            o.label?.toLowerCase().includes(props.value.toLowerCase()),
          );

          if (!hasMatches) {
            onNoOptionsClick(props.value);
            if (props.selectProps && props.selectProps.onMenuClose) {
              props.selectProps.onMenuClose();
            }
            e.preventDefault();
            return;
          }
        }

        props.onKeyDown?.(e); // Preserve default behavior
      }}
      onBlur={(e) => {
        if (isAddNew && onNoOptionsClick && props.value) {
          const filteredOptions = props.selectProps.options || [];
          const hasMatches = filteredOptions.some((o) =>
            o.label?.toLowerCase().includes(props.value.toLowerCase()),
          );
          if (!hasMatches) {
            onNoOptionsClick(props.value);
          }
        }
        if (props.onBlur) props.onBlur(e);
      }}
    />
  );
};

const CustomCreatableInput = (props) => {
  const { onKeyDown, onBlur, maxLength } = props.selectProps;

  return (
    <components.Input
      {...props}
      maxLength={maxLength}
      onKeyDown={(e) => {
        if (onKeyDown) onKeyDown(e);
        props.onKeyDown?.(e);
      }}
      onBlur={(e) => {
        if (onBlur) onBlur(e);
        props.onBlur?.(e);
      }}
    />
  );
};

const CustomOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="d-flex flex-column">
        {/* <strong style={{ textTransform: "uppercase" }}>{slice(props?.data?.label || '', 20)}</strong> */}
        <strong style={{ textTransform: "uppercase" }}>{props?.data?.label}</strong>

        <CustomTooltip text={props?.data?.description || ""}>
          <small>{slice(props?.data?.description || "", 30)}</small>
        </CustomTooltip>
      </div>
    </components.Option>
  );
};

const customFilterOption = (option, inputValue) => {
  const input = inputValue?.toLowerCase();
  const label = option?.label?.toLowerCase();
  const description = option?.data?.description?.toLowerCase();

  // 1. Strict substring match (priority)
  if (label?.includes(input) || description?.includes(input)) {
    return true;
  }

  // 2. Smart typo tolerance & fuzzy matching (via utility)
  if (input?.length >= 2) {
    return isCloseMatch(input, label) || isCloseMatch(input, description);
  }

  return false;
};

const CustomNoOptionsMessage = (props) => {
  const { isAddNew, onNoOptionsClick } = props.selectProps;
  const inputValue = props.selectProps.inputValue || "";

  // Only show "No options" message if input has at least 1 character
  if (inputValue.length < 1) {
    return isAddNew ? (
      <components.NoOptionsMessage {...props}>
        <div style={{ padding: "4px 8px" }}>Type to search or add new item</div>
      </components.NoOptionsMessage>
    ) : (
      <components.NoOptionsMessage {...props}>
        <div style={{ padding: "4px 8px" }}>No options found</div>
      </components.NoOptionsMessage>
    );
  }

  return isAddNew ? (
    <components.NoOptionsMessage {...props}>
      <div
        className="no-options-message"
        onClick={() => {
          onNoOptionsClick?.(inputValue);
          props.selectProps.onMenuClose();
        }}
        style={{
          cursor: "pointer",
          color: "var(--primary)",
          padding: "4px 8px",
        }}
      >
        No options found. Click to add Item.
      </div>
    </components.NoOptionsMessage>
  ) : (
    <components.NoOptionsMessage {...props}>
      <div style={{ padding: "4px 8px" }}>No options found.</div>
    </components.NoOptionsMessage>
  );
};

const Dropdown = memo(
  forwardRef((props, ref) => {
    const {
      options,
      isMulti,
      isDisabled,
      isClearable,
      isLoading,
      isSearchable = true,
      name,
      isRtl,
      value,
      onChange,
      label,
      placeholder,
      isRequired,
      additonalSearch,
      onKeyDown,
      onNoOptionsClick,
      isAddNew,
      creatable,
      onInputChange,
      maxLength,
      ...restProps
    } = props;
    // selectedOption was redundant for a controlled component and caused infinite loops

    const handleChange = (selectedOption) => {
      if (onChange) {
        if (isMulti) {
          // For multi-select, return array of values
          onChange(selectedOption ? selectedOption.map((option) => option.value) : []);
        } else {
          onChange(selectedOption ? selectedOption.value : "");
        }
      }
    };

    const customStyles = {
      control: (provided, state) => ({
        ...provided,
        boxShadow: state.isFocused ? null : provided.boxShadow,
        borderColor: state.isFocused ? "var(--border-light)" : provided.borderColor,
        "&:hover": {
          borderColor: state.isFocused ? "var(--secondary)" : provided.borderColor,
        },
      }),
    };

    const SelectComponent = creatable ? CreatableSelect : Select;

    return (
      <>
        <Form.Label className={isRequired ? "required" : ""} aria-label={label}>
          {label}
        </Form.Label>

        <SelectComponent
          ref={ref}
          className="basic-single mb-3"
          classNamePrefix="select"
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isRtl={isRtl}
          isSearchable={isSearchable}
          onKeyDown={onKeyDown}
          name={name}
          menuPosition="fixed" // Use fixed for dropdown visibility in modals/scrollable containers
          menuPlacement="bottom"
          menuPortalTarget={document.body} // Portal it to the body to fix positioning
          styles={{
            ...customStyles,
            menu: (provided) => ({
              ...provided,
              zIndex: 9999, // ensure it's above all UI
              minWidth: "100%",
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }), // also for portal
          }}
          components={{
            Option: CustomOption,
            Input: creatable ? CustomCreatableInput : CustomInput,
            NoOptionsMessage: CustomNoOptionsMessage,
          }}
          options={Array.isArray(options) ? options : []}
          getOptionLabel={({ label }) => label}
          getOptionValue={({ value }) => value}
          value={
            Array.isArray(options)
              ? isMulti
                ? options.filter(({ value }) => name?.some((v) => v == value))
                : (() => {
                    // Avoid `value == name` when name is "" matching numeric 0 (loose equality).
                    if (name === "" || name === null || name === undefined) {
                      return null;
                    }
                    return (
                      options.find(
                        ({ value }) => value === name || String(value) === String(name),
                      ) || (creatable && name ? { label: name, value: name } : null)
                    );
                  })()
              : null
          }
          onChange={handleChange}
          placeholder={
            placeholder === ""
              ? "Select"
              : placeholder?.toLowerCase()?.startsWith("select")
                ? placeholder
                : `Select ${placeholder || "Value"}`
          }
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
            border: 0,
            colors: {
              ...theme.colors,
              primary25: "var(--border-light)",
              primary: "var(--primary)",
            },
          })}
          filterOption={additonalSearch ? customFilterOption : undefined}
          isMulti={isMulti}
          onInputChange={onInputChange}
          tabSelectsValue={true}
          isAddNew={isAddNew}
          onNoOptionsClick={onNoOptionsClick}
          maxLength={maxLength}
          {...restProps}
        />
      </>
    );
  }),
);

export default Dropdown;
